import React, { useEffect, useState } from 'react';
import { ButcherHeader } from '@components/ButcherHeader';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { OrderProfileCard } from '@components/OrderCreation/OrderProfileCard';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import {
  errorMessage,
  fileUploadConfig,
  Length,
  orderBreadCrumbSteps,
  OrderCreationMasterData,
  WeightType,
} from '@helpers/Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField from '@mui/material/TextField';
import { useNavigate } from 'react-router-dom';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { useToast } from '@contexts/Toast';
import {
  OrderSpeciesData,
  OrderServiceProps,
  SpeciesMaster,
  PackageMaster,
  AddonsListProps,
  MasterProduct,
  NewOrderInitiationProps,
} from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import { Axios } from '@helpers/ApiService';
import { UseWarningToast } from '@contexts/WarningToast';
import {
  calculateTotalPrice,
  formatDate,
  getSumOfAddOns,
  getSumOfPackage,
  resetCustomerOrderData,
} from '@services/OrderCreationService';
import { OrderConfirmationPopup } from '@components/OrderConfirmationPopup';
import { useFetchStateName } from '@hooks/useFetchStateName';

export const OrderSummary: React.FC = () => {
  const navigate = useNavigate();
  const { getStateName } = useFetchStateName();
  const { showToastMessage } = useToast();
  const { showWarningToast } = UseWarningToast();
  const [advancePayment, setAdvancePayment] = useState<number>();

  const [estCompletionDate, setEstCompletionDate] =
    useState<dayjs.Dayjs | null>(null);
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [additionalNotes, setAdditionalNotes] = useState<string[]>([]);
  const [speciesData, setSpeciesData] = useState<OrderSpeciesData[]>();
  const [services, setServices] = useState<OrderServiceProps[]>([]);
  const [currentSpecies, setCurrentSpecies] = useState<SpeciesMaster | null>(
    null,
  );
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [packageMasters, setPackageMasters] = useState<PackageMaster[]>([]);
  const [customerId, setCustomerId] = useState<number>(0);
  const [totalDeposit, setTotalDeposit] = useState<number>();
  const [addons, setAddons] = useState<AddonsListProps[]>([]);
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();
  const [orderNumber, setOrderNumber] = useState<string>();
  const [showOrderConfirmModal, setShowOrderConfirmModal] = useState(false);

  /**
   * Initializes order-related state on component mount.
   *
   * This `useEffect` hook performs the following:
   * - Retrieves parsed order data and customer ID from `CustomerOrderService`.
   * - Extracts species-level data for the current customer.
   * - Calculates and sets the total advance payment and deposit across all species.
   * - Sets the estimated completion date if it exists.
   * - Stores species data in local state.
   * - If a `speciesId` is available, fetches all master data associated with it.
   * - Also extracts and sets arrays of individual `additionalNotes`, `advancePayment`, and `depositAmount` values
   *   for form prepopulation or further use.
   *
   * Dependencies: Runs only once on initial render (`[]`).
   */

  useEffect(() => {
    const parsedOrderData = CustomerOrderService.getParsedOrderData();
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);
    const customerId = CustomerOrderService.getCustomerId();
    setCustomerId(customerId);

    const currentCustomerOrderData = parsedOrderData[customerId];
    const OrderDataSpecies = currentCustomerOrderData.species;

    const allSpeciesCompleted =
      Array.isArray(OrderDataSpecies) &&
      OrderDataSpecies.every(
        (speciesItem: { isCustomizationCompleted: number }) => {
          // Check whether all of the species have completed the customization details, proceed otherwise navigate
          // back to Species customization screen.
          const isCompleted =
            speciesItem.isCustomizationCompleted == 1 ? true : false;
          return isCompleted;
        },
      );

    if (!allSpeciesCompleted) {
      showToastMessage(errorMessage.ORDER_INCOMPLETION_ERROR, 'error');
      setTimeout(() => {
        navigate(FrontendRoutes.CREATE_ORDER_SPECIES_CUSTOMISATION);
      }, fileUploadConfig.NETWORK_DELAY_MS);
      return;
    }

    const totalDeposit = OrderDataSpecies.reduce(
      (speciesSum: number, species) => {
        const serviceDeposits = Array.isArray(species.services)
          ? species.services.reduce(
              (serviceSum: number, service: { depositAmount: number }) => {
                return service.depositAmount
                  ? serviceSum + service.depositAmount
                  : serviceSum;
              },
              0,
            )
          : 0;
        return speciesSum + serviceDeposits;
      },
      0,
    );

    setTotalDeposit(totalDeposit);

    if (currentCustomerOrderData.estCompletionDate) {
      setEstCompletionDate(dayjs(currentCustomerOrderData.estCompletionDate));
    } else {
      setEstCompletionDate(null);
    }
    setSpeciesData(OrderDataSpecies);

    if (currentCustomerOrderData.speciesId) {
      fetchAddons();
      fetchMasterPackages();
      fetchMasterProducts();
      fetchEstimationCompletionDate();
      fetchMasters(currentCustomerOrderData.speciesId);

      const notes = OrderDataSpecies.map(
        (s: { additionalNotes: string }) => s.additionalNotes ?? '',
      );
      setAdvancePayment(currentCustomerOrderData.advancePayment);
      setAdditionalNotes(notes);
    }
  }, []);

  /**
   * Fetches all master data related to a specific species and updates state accordingly.
   *
   * This function retrieves the following master data in parallel:
   * - Services
   * - Species
   * - Products
   * - Add-ons
   * - Packaging
   *
   * Once the data is fetched, it sets the relevant state variables:
   * - `setServices` with services data.
   * - `setProductMasters` with product master data.
   * - `setAddonsMasters` with add-ons data.
   * - `setPackageMasters` with packaging type data.
   * - `setCurrentSpecies` with the species that matches the provided `speciesId`.
   *
   * @param {number} speciesId - The ID of the species to fetch related master data for.
   *
   * @returns {Promise<any>} A promise that resolves when all data is fetched and state is updated.
   */
  const fetchMasterDataWithSpeciesId = async (
    speciesId: number,
    type: number,
  ): Promise<any> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        speciesId,
        masterDataType: type,
      });

      return response.data?.data?.masterData;
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches master data from the server based on the provided master data type.
   *
   * This function sends a POST request to the `FETCH_SPECIES_MASTERDATA` API endpoint with
   * a `masterDataType` parameter that determines which type of master data to retrieve
   * (e.g., product, package, estimation date).
   *
   * On a successful response, the `masterData` object from the API payload is returned.
   * If the request fails, an error toast message is displayed and the error is rethrown
   * for higher-level error handling.
   *
   * @async
   * @function fetchMasterData
   * @param {number} masterDataType - An identifier representing the type of master data to fetch.
   * @returns {Promise<any>} A promise resolving to the master data object returned by the API.
   *
   * @throws {Error} Will throw the caught error after showing an error toast.
   *
   */
  const fetchMasterData = async (masterDataType: number): Promise<any> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType,
      });
      return response.data.data.masterData;
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
      throw error;
    }
  };

  /**
   * Fetches the master data for product packaging options.
   *
   * Calls `fetchMasterData` with the `PACKAGE_MASTER_DATA` type and updates
   * the component state with the retrieved `packageType` array.
   *
   * @async
   * @function fetchMasterPackages
   * @returns {Promise<void>} A promise that resolves once the packaging data
   *          has been successfully fetched and stored in state.
   */
  const fetchMasterPackages = async (): Promise<void> => {
    const masterData = await fetchMasterData(
      OrderCreationMasterData.PACKAGE_MASTER_DATA,
    );
    setPackageMasters(masterData.packageType);
  };

  /**
   * Fetches the estimated order completion date.
   *
   * Calls `fetchMasterData` with the `ORDER_ESTIMATION_DATE` type, converts the
   * response into a `dayjs` object, and stores it in state.
   *
   * @async
   * @function fetchEstimationCompletionDate
   * @returns {Promise<void>} A promise that resolves once the estimation date
   *          has been successfully fetched and stored in state.
   */
  const fetchEstimationCompletionDate = async (): Promise<void> => {
    const masterData = await fetchMasterData(
      OrderCreationMasterData.ORDER_ESTIMATION_DATE,
    );
    setEstCompletionDate(dayjs(masterData));
  };

  /**
   * Fetches the master data for products.
   *
   * Calls `fetchMasterData` with the `PRODUCT_MASTER_DATA` type and updates
   * the component state with the retrieved product master list.
   *
   * @async
   * @function fetchMasterProducts
   * @returns {Promise<void>} A promise that resolves once the product data
   *          has been successfully fetched and stored in state.
   *
   */
  const fetchMasterProducts = async (): Promise<void> => {
    const masterData = await fetchMasterData(
      OrderCreationMasterData.PRODUCT_MASTER_DATA,
    );
    setMasterProducts(masterData);
  };

  /**
   * Fetches the list of available add-ons for product customization.
   *
   * This function sends a POST request to the `FETCH_ADDONS` API endpoint.
   * Upon successful response, it sets the retrieved add-ons into the component state
   * using `setAddons`. The expected response contains an `addOns` array.
   *
   * In case of a request failure, an error toast is displayed and the error is rethrown
   * for further handling.
   *
   * @returns {Promise<void>} A promise that resolves when the add-ons are successfully fetched and set.
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      setAddons(response.data.data.addOns);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches all master data related to a given species and updates component state accordingly.
   *
   * This function:
   * - Uses `Promise.all` to concurrently fetch master data for:
   *    - Services (typeId: 3)
   *    - Species list (typeId: 1)
   * - Updates the local state with:
   *    - List of available services
   * - Attempts to find and set the current species from the fetched species data.
   *
   * @param {number} speciesId - The ID of the species for which master data is to be fetched.
   */
  const fetchMasters = async (speciesId: number): Promise<void> => {
    try {
      const [servicesData, speciesData] = await Promise.all([
        fetchMasterDataWithSpeciesId(
          speciesId,
          OrderCreationMasterData.SERVICE_MASTER_DATA,
        ),
        fetchMasterDataWithSpeciesId(
          speciesId,
          OrderCreationMasterData.SPECIES_MASTER_DATA,
        ),
      ]);
      setServices(servicesData?.services ?? []);
      const foundSpecies = speciesData?.find(
        (species: SpeciesMaster) => species.id === speciesId,
      );
      setCurrentSpecies(foundSpecies ?? null);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Toggles the open/closed state of an accordion section by index.
   *
   * If the index is already in the `openAccordions` array, it will be removed (collapsed).
   * If it's not present, it will be added (expanded).
   *
   * @param {number} index - The index of the accordion section to toggle.
   */
  const toggleAccordion = (index: number): void => {
    setOpenAccordions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  /**
   * Handles the creation and processing of a customer order.
   *
   * This function performs the following steps:
   * 1. Retrieves the parsed customer order data and current customer ID.
   * 2. Formats the estimated completion date and adds it to the payload.
   * 3. Updates the local storage with the modified order data.
   * 4. Validates that all species are fully customized.
   * 5. Sends a POST request to the server to create the order.
   * 6. On success, shows a success toast and navigates to the order confirmation screen.
   * 7. On failure, shows an error toast and throws the error.
   *
   * @async
   * @function
   * @returns {Promise<void>} - A promise that resolves once the order has been created or an error is handled.
   */
  const handleCreateAndProcessOrder = async (): Promise<void> => {
    const parsedOrderData = CustomerOrderService.getParsedOrderData();
    const currentCustomerOrderData = parsedOrderData[customerId];
    const finalEstCompletionDate = estCompletionDate?.format('YYYY-MM-DD');
    if (estCompletionDate == null) {
      showToastMessage(errorMessage.ORDER_ESTIMATION_DATE_REQUIRED, 'error');
      return;
    }
    const payload = {
      ...currentCustomerOrderData,
      estCompletionDate: finalEstCompletionDate,
      advancePayment: advancePayment,
    };

    const updatedOrderData = {
      ...parsedOrderData,
      [customerId]: {
        ...currentCustomerOrderData,
        advancePayment: advancePayment,
        estCompletionDate: finalEstCompletionDate,
      },
    };

    CustomerOrderService.setParsedOrderData(updatedOrderData);

    // Validate species customization
    const { species: speciesList, quantity } = currentCustomerOrderData;
    if (!speciesList || speciesList.length !== quantity) {
      showWarningToast(
        errorMessage.ORDER_SPECIES_CUSTOMIZATION_FAILURE,
        'Customization Incomplete',
        () => navigate(FrontendRoutes.CREATE_ORDER_SPECIES_CUSTOMISATION),
      );
      return;
    }

    try {
      const response = await Axios.post(ApiEndpoints.CREATE_ORDER, payload);
      const orderId = response.data.data.orderId;

      setOrderNumber(response.data.data.orderNumber);
      setShowOrderConfirmModal(true);
      // Removing the order data from local storage
      resetCustomerOrderData();
      setTimeout(() => {
        navigate(FrontendRoutes.ORDER_DETAILS, {
          state: { orderId },
        });
      }, fileUploadConfig.NETWORK_DELAY_MS);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_CREATING_ORDER, 'error');
    }
  };

  /**
   * Calculates the total price of a product including its base price, add-ons, and packaging.
   *
   * @param {Object} product - The product details.
   * @param {number} product.quantity - Quantity of the product.
   * @param {number[]} product.addOnIds - List of selected add-on IDs.
   * @param {Object[]} product.packaging - List of selected packaging options.
   * @param {number} product.packaging[].productPackageTypeId - The ID of the packaging type.
   *
   * @param {Object} master - The master product containing base pricing.
   * @param {number} master.price - Base price of the product.
   *
   * @returns {number} The total calculated price for the product including base price, add-ons, and packaging.
   */
  const handleSpeciesEdit = (index: number) => {
    localStorage.setItem('currentCustomizingId', index.toString());
    navigate(FrontendRoutes.CREATE_ORDER_CHECKIN);
  };
  return (
    <div className="flex h-screen w-screen">
      <SideNav active={2} />
      <div className="flex-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />

        {/* Order Breadcrumb */}
        <div className="px-2">
          <OrderBreadcrumb
            steps={orderBreadCrumbSteps}
            customerName={`${customerDetails?.firstName} ${customerDetails?.lastName}`}
            activeItem="Order Summary"
            customerId={customerId}
          />
        </div>

        {/* Main Content */}
        <div className="p-3">
          <OrderProfileCard />
        </div>
        <div className="p-3 pb-16">
          {/* Order Confirm Modal */}
          {showOrderConfirmModal && (
            <OrderConfirmationPopup
              orderNumber={orderNumber}
              visible={showOrderConfirmModal}
              onClose={() => setShowOrderConfirmModal(false)}
            />
          )}

          {speciesData?.map((species, index) => (
            <div key={index}>
              <div className="border rounded-md shadow-sm mb-3">
                <div className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg bg-white">
                  <div className="flex-1 text-left">
                    {species.speciesName}
                    {species.checkIn.feet && species.checkIn.inches && (
                      <>
                        {''} ({species.checkIn.feet} {Length.FEET}{' '}
                        {species.checkIn.inches} {Length.INCHES})
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleSpeciesEdit(index)}
                      type="button"
                      className="text-primary font-semibold flex items-center gap-1"
                    >
                      <FontAwesomeIcon icon={faPen} />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleAccordion(index)}
                    >
                      <FontAwesomeIcon
                        className="text-[#000A16]"
                        icon={
                          openAccordions.includes(index)
                            ? faChevronUp
                            : faChevronDown
                        }
                      />
                    </button>
                  </div>
                </div>

                {openAccordions.includes(index) && (
                  <div className="p-4">
                    {/* Summary Section */}
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Summary</h2>
                      </div>

                      {/* Check-in */}
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold ml-2">Check-In</h3>
                        <button
                          onClick={() =>
                            navigate(FrontendRoutes.CREATE_ORDER_CHECKIN)
                          }
                          type="button"
                          className="text-primary font-semibold"
                        >
                          <FontAwesomeIcon icon={faPen} /> Edit
                        </button>
                      </div>
                      <div className="mt-4 p-3 rounded-lg shadow-md bg-quaternary font-medium">
                        {/* Species State and License Number */}
                        {species.checkIn.licenseNumber && (
                          <div className="bg-white p-4 rounded-md border mb-2">
                            <div className="flex w-full justify-start">
                              <div className="w-1/2">
                                <span className="block mb-2 text-[#000A1666]">
                                  Species State
                                </span>
                                <span>
                                  {getStateName(
                                    Number(species.checkIn.harvestStateId),
                                  )}
                                </span>
                              </div>
                              <div className="w-1/2">
                                <span className="block mb-2 text-[#000A1666]">
                                  License Number
                                </span>
                                <div className="flex justify-between">
                                  <span>{species.checkIn.licenseNumber}</span>
                                  <span>
                                    Expiry:{' '}
                                    {formatDate(
                                      customerDetails?.HuntingLicences?.find(
                                        (license) =>
                                          license.id ===
                                          species.checkIn.huntingLicenceId,
                                      )?.expiryDate || '',
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Permit, CITES and Confirmation Number */}
                        {(species.checkIn.permitNumber ||
                          species.checkIn.citesNumber ||
                          species.checkIn.conformationNumber) && (
                          <div className="bg-white p-4 rounded-md border mb-2">
                            <div className="flex w-full justify-start">
                              {species.checkIn.permitNumber && (
                                <div className="w-1/2">
                                  <span className="block mb-2 text-[#000A1666] font-medium">
                                    Permit Number
                                  </span>
                                  <span>{species.checkIn.permitNumber}</span>
                                </div>
                              )}
                              {species.checkIn.citesNumber && (
                                <div className="w-1/2">
                                  <span className="block mb-2 text-[#000A1666] font-medium">
                                    CITES Number
                                  </span>
                                  <span>{species.checkIn.citesNumber}</span>
                                </div>
                              )}
                            </div>
                            {species.checkIn.conformationNumber && (
                              <div className="w-full mt-3">
                                <span className="block mb-2 text-[#000A1666] font-medium">
                                  Confirmation Number
                                </span>
                                <span>
                                  {species.checkIn.conformationNumber}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Length and Weight */}
                        {(species.checkIn.feet || species.checkIn.weight) && (
                          <div className="bg-white p-4 rounded-md border mb-2">
                            <div className="flex w-full justify-start">
                              {species.checkIn.feet && (
                                <div className="w-1/2">
                                  <span className="block mb-2 text-[#000A1666] font-medium">
                                    Length
                                  </span>
                                  <span>
                                    {species.checkIn.feet} Feet{' '}
                                    {species.checkIn.inches} Inches
                                  </span>
                                </div>
                              )}
                              {species.checkIn.weight && (
                                <div className="w-1/2">
                                  <span className="block mb-2 text-[#000A1666] font-medium">
                                    Weight
                                  </span>
                                  <span>{species.checkIn.weight}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {species?.checkIn?.notes && (
                          <div className="bg-white p-4 rounded-md border mb-2 mt-4">
                            <h4 className="font-medium mb-2">
                              Check - in notes:
                            </h4>
                            <div className="space-y-2">
                              <span className="text-[#43474E]">
                                {species.checkIn.notes}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Services Section */}
                    {!species?.services?.length ? (
                      <div className="mt-3">
                        <h3 className="text-lg font-semibold ml-2 mb-4">
                          Services
                        </h3>
                        <div className="bg-quaternary p-6 rounded-lg shadow-sm border text-center">
                          <h3 className="text-lg font-medium text-[#1C1B1F] mb-2">
                            No service selected! Go back and add services
                          </h3>
                          <div className="relative">
                            <button
                              type="button"
                              className="text-primary font-bold"
                            >
                              Select Service
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-semibold ml-2">
                            Services
                          </h3>
                        </div>

                        <div className="mt-3">
                          {species?.services?.map((service) => {
                            const master = services.find(
                              (m) => m.id === service.serviceId,
                            );
                            if (!master) return null;

                            return (
                              <div
                                key={service.serviceId}
                                className="p-4 rounded-md border bg-quaternary mb-4"
                              >
                                <div className="flex gap-4 items-start">
                                  {/* Image */}
                                  <img
                                    src={currentSpecies?.imageUrl}
                                    alt={currentSpecies?.name}
                                    className="w-16 h-16 object-cover rounded-md"
                                  />

                                  {/* Main content */}
                                  <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                      {/* Service Name */}
                                      <p className="text-gray-900 font-semibold text-base">
                                        {master.name}
                                      </p>
                                      {/* Rate */}
                                      <p className="text-gray-900 font-semibold text-sm whitespace-nowrap">
                                        ${master.rate}/{master.unit}
                                      </p>
                                    </div>
                                    <div className="bg-white p-2 mt-2 rounded-md border text-gray-600 text-sm w-fit">
                                      Total Price to be calculated after
                                      processing
                                    </div>

                                    {/* Deposit */}
                                    {service.depositAmount && (
                                      <div className="bg-[#F5F5F5] p-2 mt-2 rounded-md border flex justify-between items-center text-sm text-gray-600">
                                        <span className="font-semibold">
                                          Deposit Collected
                                        </span>
                                        <span className="text-gray-800 font-semibold">
                                          ${service.depositAmount}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* Products and Packaging */}
                    {!species?.products?.length ? (
                      <div className="mt-3">
                        <h3 className="text-lg font-semibold ml-2 mb-4">
                          Products and Packaging
                        </h3>
                        <div className="bg-quaternary p-6 rounded-lg shadow-sm border text-center">
                          <h3 className="text-lg font-medium text-[#1C1B1F] mb-2">
                            No products selected! Go back and add products
                          </h3>
                          <div className="relative">
                            <button
                              type="button"
                              className="text-primary font-bold"
                            >
                              Select Products
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-lg">
                        <div className="flex justify-between items-center">
                          <h3 className="text-lg font-semibold mt-3 ml-2">
                            Products and Packaging
                          </h3>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(FrontendRoutes.CREATE_ORDER_PRODUCT)
                            }
                            className="text-primary font-semibold"
                          >
                            <FontAwesomeIcon icon={faPen} /> Edit
                          </button>
                        </div>

                        <div className="mt-4 rounded-lg">
                          <div>
                            <div className="bg-quaternary p-2 rounded-lg">
                              {species?.products?.map((product) => {
                                const master = masterProducts.find(
                                  (p) => p.id === product.productId,
                                );
                                return (
                                  <div key={product.productId} className="mb-4">
                                    <div className="p-4">
                                      <div className="flex justify-between items-baseline mb-1">
                                        <h4 className="font-semibold text-lg">
                                          {master?.name}
                                          {product.fatPercent > 0 &&
                                            `, ${product.fatPercent}% Fat`}
                                        </h4>
                                        {master && (
                                          <span className="text-gray-900 font-bold text-xl">
                                            $
                                            {calculateTotalPrice(
                                              product.quantity,
                                              product.addOnIds,
                                              product.packaging.map(
                                                (productPackage) => ({
                                                  productPackageTypeId:
                                                    productPackage.productPackageTypeId,
                                                  packingPercentage:
                                                    productPackage.percentage,
                                                }),
                                              ),
                                              master,
                                              addons,
                                              packageMasters,
                                            ).toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex justify-between items-baseline">
                                        <p className="text-gray-600">
                                          Total Quantity: {product.quantity}
                                        </p>
                                        <span className="text-gray-800">
                                          $
                                          {(
                                            product.quantity * master!.price
                                          ).toFixed(2)}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Add-ons */}
                                    {product.addOnIds?.length > 0 && master && (
                                      <div className="bg-white p-4 rounded-md border mb-4">
                                        <h4 className="font-medium mb-2">
                                          Add-ons:
                                          {master?.upchargeIngredientsFlag ===
                                          0 ? (
                                            <span className="ml-2 text-lg font-semibold">
                                              (No Up-Charge)
                                            </span>
                                          ) : null}
                                        </h4>
                                        <div className="space-y-2">
                                          {getSumOfAddOns(
                                            product.addOnIds,
                                            addons,
                                            product.quantity,
                                            master,
                                          ).map((addon) => {
                                            if (!addon) return null; // skip if addons not present
                                            return (
                                              <div
                                                key={addon.id}
                                                className="flex justify-between"
                                              >
                                                <span className="w-1/2">
                                                  {addon.name}
                                                </span>
                                                <span className="w-1/2 text-right">
                                                  Total: $
                                                  {addon.total.toFixed(2)}
                                                </span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {/* Packaging */}
                                    {product.packaging?.length > 0 &&
                                      master && (
                                        <div className="bg-white p-4 rounded-md border mb-4">
                                          <h4 className="font-medium mb-2 text-[#43474E]">
                                            Packaging:
                                          </h4>
                                          <div className="space-y-2">
                                            {getSumOfPackage(
                                              product.quantity,
                                              product.packaging.map((p) => ({
                                                productPackageTypeId:
                                                  p.productPackageTypeId,
                                                packingPercentage: p.percentage,
                                              })),
                                              packageMasters,
                                              master,
                                            ).map((packageTotal) => (
                                              <div
                                                key={packageTotal.packageId}
                                                className="flex justify-between text-sm text-gray-700"
                                              >
                                                <span className="w-1/4">
                                                  {packageTotal.name}
                                                </span>
                                                <span className="w-1/4 text-center">
                                                  Size: {packageTotal.size}{' '}
                                                  {WeightType.LBS}
                                                </span>
                                                {product.packaging.length >
                                                  1 && (
                                                  <span className="w-1/4">
                                                    <span className="text-[#43474E]">
                                                      Pkg Split:
                                                    </span>{' '}
                                                    {packageTotal.percentage}%
                                                  </span>
                                                )}
                                                <span className="w-1/4 text-right">
                                                  Total: $
                                                  {packageTotal.total.toFixed(
                                                    2,
                                                  )}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                    {/* Packaging Notes */}
                                    {product.packagingNotes && (
                                      <div className="bg-white p-4 rounded-md border mb-4">
                                        <h4 className="font-medium mb-2">
                                          Packaging Notes:{' '}
                                        </h4>
                                        <div className="space-y-2">
                                          <span className="text-[#43474E]">
                                            {product.packagingNotes}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Additional Notes */}
                    <div className="mt-2">
                      <div className="flex justify-between items-center mb-2 mt-3">
                        <h3 className="text-lg font-semibold ml-2">
                          Additional Notes
                        </h3>
                      </div>
                      <textarea
                        placeholder="Add notes or description..."
                        className="w-full border p-2 rounded-md h-25 focus:outline-primary"
                        value={additionalNotes[index] || ''}
                        maxLength={100}
                        readOnly
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="border rounded-md shadow-sm p-2 mb-3 border-b pb-2">
            <div className="flex justify-between items-center">
              <span className="text-[#1C1B1F] font-medium">
                Estimated completion date
              </span>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disablePast
                  value={estCompletionDate}
                  onChange={(newValue) => setEstCompletionDate(newValue)}
                  desktopModeMediaQuery="@media (min-width: 0px)"
                  slots={{ textField: TextField }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        '& .MuiInputBase-root': {
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          paddingY: '4px',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
          {/* Advance Payment */}
          <div className="flex justify-between items-center bg-white mt-2 border-b pb-2">
            <span className="text-gray-700 font-medium pl-4">
              Advance Payment Made
            </span>
            <div className="relative w-28">
              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                $
              </span>
              <input
                type="text"
                inputMode="decimal"
                pattern="^(0|[1-9]\d*)(\.\d*)?$"
                value={advancePayment === undefined ? '' : advancePayment}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow: 0, 0.5, 1, 1.5, 10, 10.5, 100, 100.5, etc. Disallow: 00, 01, 012, .5, -1, etc.
                  if (
                    value === '' ||
                    /^0(\.\d*)?$/.test(value) ||
                    /^[1-9]\d*(\.\d*)?$/.test(value)
                  ) {
                    setAdvancePayment(value === '' ? undefined : Number(value));
                  }
                }}
                className="pl-5 pr-2 py-2 border rounded-md font-semibold w-full text-right focus:outline-primary"
              />
            </div>
          </div>
          <div className="border rounded-md shadow-sm p-1 bg-quaternary mt-3">
            <div className="flex justify-between items-center bg-quaternary ">
              <span className="text-[#1C1B1F] font-medium">
                Total Deposits Made:
              </span>
              <span className="font-semibold text-[#43474E] py-2 px-3  min-w-[100px] inline-block text-center">
                ${totalDeposit ?? 0}
              </span>
            </div>
          </div>
        </div>
        <div className="flex justify-between gap-4 fixed bottom-0 left-14 w-[calc(100%-64px)] p-4 ">
          <button
            onClick={() =>
              navigate(FrontendRoutes.CREATE_ORDER_SPECIES_CUSTOMISATION)
            }
            type="button"
            className="w-[50%] px-6 py-2 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Back
          </button>
          <button
            onClick={handleCreateAndProcessOrder}
            type="button"
            className="w-[50%] px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
          >
            Create & Process Order
          </button>
        </div>
      </div>
    </div>
  );
};
